import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { LEAD_SOURCE_OPTIONS, PROPERTY_TYPE_OPTIONS } from "./reportingConfig";

// ─── Master Customer roster — bulk upload ────────────────────────────────────
// One row per customer. Four columns; all optional individually but a row
// needs at least one filled cell to count.

const COLUMNS = [
  { key: "name", header: "Customer Name", width: 26 },
  { key: "phone", header: "Customer Phone", width: 20 },
  { key: "lead", header: "Lead", width: 16 },
  { key: "propertyType", header: "Type", width: 16 },
  { key: "budget", header: "Budget", width: 16 },
  { key: "createdDate", header: "Month", width: 14 },
];

// header text (any case / spacing / punctuation) → column key
const HEADER_ALIASES = {
  "customer name": "name",
  customer: "name",
  name: "name",
  "customer phone": "phone",
  phone: "phone",
  "phone number": "phone",
  mobile: "phone",
  "customer mobile": "phone",
  lead: "lead",
  "lead source": "lead",
  source: "lead",
  type: "propertyType",
  "property type": "propertyType",
  "property": "propertyType",
  budget: "budget",
  "budget range": "budget",
  month: "createdDate",
  date: "createdDate",
  "created date": "createdDate",
  "date added": "createdDate",
};

// Excel/CSV month cells commonly arrive as "YYYY-MM", "MM/YYYY", a full date,
// or an ExcelJS Date object — normalise all of them to "YYYY-MM-DD" (first of
// the month when only a month is given) so it matches the app's date fields.
const normalizeDateCell = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const raw = String(value || "").trim();
  if (!raw) return "";
  const isoMonth = raw.match(/^(\d{4})-(\d{1,2})$/);
  if (isoMonth) return `${isoMonth[1]}-${isoMonth[2].padStart(2, "0")}-01`;
  const isoDate = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoDate) {
    return `${isoDate[1]}-${isoDate[2].padStart(2, "0")}-${isoDate[3].padStart(2, "0")}`;
  }
  const slashMonth = raw.match(/^(\d{1,2})\/(\d{4})$/);
  if (slashMonth) return `${slashMonth[2]}-${slashMonth[1].padStart(2, "0")}-01`;
  const slashDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashDate) {
    return `${slashDate[3]}-${slashDate[1].padStart(2, "0")}-${slashDate[2].padStart(2, "0")}`;
  }
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return normalizeDateCell(parsed);
  return "";
};

const normHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const readCell = (cell) => {
  if (!cell) return "";
  const v = cell.value;
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v;
  if (typeof v === "object") {
    if (Array.isArray(v.richText))
      return v.richText.map((part) => part.text).join("");
    if (v.text !== undefined) return v.text;
    if (v.result !== undefined) return v.result;
    return "";
  }
  return v;
};

// ── Template ────────────────────────────────────────────────────────────────
export const downloadCustomerTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Customers");
  sheet.columns = COLUMNS.map((c) => ({
    header: c.header,
    key: c.key,
    width: c.width,
  }));
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF1F2937" },
  };
  [
    ["Rahul Sharma", "9812345678", "Walk In", "Flat", "1-50 L", "2026-01"],
    ["Priya Verma", "9800011122", "Social Media", "Villa", "51 L-1 Cr", "2026-02"],
    ["", "9700033344", "", "", "", ""],
  ].forEach((row) => sheet.addRow(row));

  // Dropdown validation on Lead / Type so a filled-in cell is always one of
  // the app's actual options, for every row the template pre-formats. Budget
  // is free text, no fixed list.
  const dropdownColumns = [
    ["lead", LEAD_SOURCE_OPTIONS],
    ["propertyType", PROPERTY_TYPE_OPTIONS],
  ];
  const lastRow = 500;
  dropdownColumns.forEach(([key, options]) => {
    const colIndex = COLUMNS.findIndex((c) => c.key === key) + 1;
    if (!colIndex) return;
    const formula = `"${options.join(",")}"`;
    for (let r = 2; r <= lastRow; r += 1) {
      sheet.getCell(r, colIndex).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [formula],
        showErrorMessage: true,
        errorTitle: "Invalid value",
        error: `Pick one of: ${options.join(", ")}`,
      };
    }
  });

  const notes = workbook.addWorksheet("Instructions");
  notes.getColumn(1).width = 100;
  [
    "MASTER CUSTOMER ROSTER — BULK UPLOAD",
    "",
    "• One row per customer.",
    "• Columns: Customer Name | Customer Phone | Lead | Type | Budget | Month",
    "  Lead: Social Media or Walk In. Type: Flat, Villa, Kothi, Plot or",
    "  Commercial. Budget: free text, e.g. 75 L or 1.2 Cr.",
    "  Month: the month (or exact date) the customer was added, e.g. 2026-01",
    "  or 15/01/2026 — defaults to today if left blank.",
    "• Column order can vary; the header text is what matters.",
    "• A row needs at least one non-empty cell.",
    "• A customer already in the roster with the same phone (or same name when",
    "  there is no phone) is skipped, so re-uploading the same file is safe.",
    "• Name / phone can still be edited afterwards until the customer's first",
    "  remark is logged; after that the record locks.",
  ].forEach((line) => notes.addRow([line]));

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "customer-roster-template.xlsx",
  );
};

// ── Download customer roster with full remarks history ───────────────────────
export const downloadMasterCustomersExcel = async (customers, formType) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Customers");

  sheet.columns = [
    { header: "#", key: "seq", width: 6 },
    { header: "Customer Name", key: "name", width: 26 },
    { header: "Customer Phone", key: "phone", width: 18 },
    { header: "Lead", key: "lead", width: 16 },
    { header: "Property Type", key: "propertyType", width: 16 },
    { header: "Budget", key: "budget", width: 16 },
    { header: "Month", key: "month", width: 14 },
    { header: "Remarks", key: "remarks", width: 60 },
  ];

  const hdr = sheet.getRow(1);
  hdr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hdr.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F2937" } };
  hdr.alignment = { vertical: "middle" };
  sheet.getRow(1).height = 22;

  // Archived customers are left out of the download.
  const arr = (Array.isArray(customers) ? customers : []).filter(
    (c) => !c.archived,
  );
  arr.forEach((c, idx) => {
    const remarks = Array.isArray(c.dailyRemarks) ? c.dailyRemarks : [];
    const remarkText = remarks
      .slice()
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
      .map((r) => {
        const d = r.date ? new Date(r.date).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "";
        const by = r.by ? ` (${r.by})` : "";
        return d ? `${d}: ${r.remarks || ""}${by}` : `${r.remarks || ""}${by}`;
      })
      .filter(Boolean)
      .join("\n");

    const row = sheet.addRow({
      seq: idx + 1,
      name: c.name || "",
      phone: c.phone || "",
      lead: c.lead || "",
      propertyType: c.propertyType || "",
      budget: c.budget || "",
      month: c.createdDate
        ? new Date(c.createdDate).toLocaleDateString("en-IN", { month: "short", year: "numeric" })
        : "",
      remarks: remarkText,
    });
    row.alignment = { vertical: "top", wrapText: true };
    if (remarkText.includes("\n")) {
      const lineCount = remarkText.split("\n").length;
      row.height = Math.min(15 + lineCount * 14, 150);
    }
    row.getCell("seq").alignment = { vertical: "top", horizontal: "center" };
  });

  const typeName = formType === 3 ? "TeleCalling" : formType === 4 ? "RealEstateSales" : "Customers";
  const dateStr = new Date().toISOString().slice(0, 10);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${typeName}-CustomerRoster-${dateStr}.xlsx`,
  );
};

// Minimal CSV row splitter — handles quoted fields with embedded commas.
const splitCsvLine = (line) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
};

const rowsFromMatrix = (matrix) => {
  const rows = [];
  const warnings = [];
  if (!matrix.length) return { rows, warnings: ["The file is empty."] };

  const headerCells = matrix[0];
  const colOf = {};
  headerCells.forEach((cell, idx) => {
    const key = HEADER_ALIASES[normHeader(cell)];
    if (key && colOf[key] === undefined) colOf[key] = idx;
  });
  if (!Object.keys(colOf).length) {
    return {
      rows,
      warnings: [
        "No recognised column headers. Expected: Customer Name, Customer Phone, Lead, Type, Budget, Month.",
      ],
    };
  }

  const pick = (arr, key) => {
    const idx = colOf[key];
    return idx === undefined ? "" : String(arr[idx] ?? "").trim();
  };

  for (let r = 1; r < matrix.length; r += 1) {
    const arr = matrix[r] || [];
    const entry = {
      name: pick(arr, "name"),
      phone: pick(arr, "phone"),
      lead: pick(arr, "lead"),
      propertyType: pick(arr, "propertyType"),
      budget: pick(arr, "budget"),
      createdDate: normalizeDateCell(pick(arr, "createdDate")),
    };
    if (!entry.name && !entry.phone) {
      if (arr.some((cell) => String(cell || "").trim())) {
        warnings.push(`Row ${r + 1}: no customer name or phone — skipped.`);
      }
      continue;
    }
    rows.push(entry);
  }
  return { rows, warnings };
};

// ── Parse an uploaded .xlsx / .csv into { rows, warnings } ───────────────────
export const parseCustomerWorkbook = async (file) => {
  const isCsv = String(file?.name || "")
    .toLowerCase()
    .endsWith(".csv");

  if (isCsv) {
    const text = await file.text();
    const matrix = text
      .split(/\r?\n/)
      .filter((line) => line.trim().length)
      .map(splitCsvLine);
    return rowsFromMatrix(matrix);
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) return { rows: [], warnings: ["The file has no worksheets."] };

  const matrix = [];
  sheet.eachRow((row) => {
    const arr = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const v = readCell(cell);
      arr[colNumber - 1] = v instanceof Date ? normalizeDateCell(v) : String(v ?? "");
    });
    matrix.push(arr);
  });
  return rowsFromMatrix(matrix);
};
